import { MessageSquare, MessageSquareOff } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import { Member } from "../../features/members/models/Member";
import { setMemberMessagesStopped } from "../../features/members/slice-extensions/member-messages-stop-slice-extension";
import { formatMediumDate } from "../../features/utils/displayFormat";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

interface MemberMessagesStopCardProps {
  member: Member;
}

/**
 * The instructor's own lever (US-111, PD-0054): stop a member's messages to the school, or allow them
 * again. The member keeps their lessons, payments and library.
 */
const MemberMessagesStopCard = ({ member }: MemberMessagesStopCardProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [busy, setBusy] = useState(false);
  const stoppedAt = member.messagesStoppedAt;

  const toggle = async () => {
    setBusy(true);
    const result = await dispatch(setMemberMessagesStopped({ memberId: member.id, stopped: stoppedAt === null }));
    setBusy(false);
    if (setMemberMessagesStopped.rejected.match(result)) toast.error(t("members.messagesStop.failed"));
  };

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6" data-testid="member-messages-stop">
      <h2 className="text-lg font-semibold text-grey-20 dark:text-white">{t("members.messagesStop.title")}</h2>
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
        {stoppedAt
          ? t("members.messagesStop.since", { date: formatMediumDate(stoppedAt.toISOString(), getDisplayLocale()) })
          : t("members.messagesStop.hint")}
      </p>
      <button type="button" className="btn btn-secondary mt-4 flex items-center gap-1.5" disabled={busy} onClick={toggle}>
        {stoppedAt ? (
          <MessageSquare size={16} aria-hidden="true" />
        ) : (
          <MessageSquareOff size={16} aria-hidden="true" />
        )}
        {stoppedAt ? t("members.messagesStop.allow") : t("members.messagesStop.stop")}
      </button>
    </div>
  );
};

export default MemberMessagesStopCard;
